import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { AnimatedWords } from "@/components/motion";

const REVIEWS = [
  {
    name: "Aaliyah",
    role: "Member",
    quote:
      "Trainova has transformed my workout experience! I finally found someone with the same goals and energy as me!",
  },
  {
    name: "David",
    role: "Member",
    quote: "The app feels clean, motivating and easy to use!",
  },
  {
    name: "Rachel",
    role: "Member",
    quote:
      "Trainova gives you that extra push on days you don't feel motivated. The matching system is actually good.",
  },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const paginate = useCallback((d) => {
    setDir(d);
    setIndex((prev) => (prev + d + REVIEWS.length) % REVIEWS.length);
  }, []);

  useEffect(() => {
    const t = setInterval(() => paginate(1), 6000);
    return () => clearInterval(t);
  }, [paginate]);

  const review = REVIEWS[index];

  return (
    <section id="reviews" data-testid="reviews-section" className="relative overflow-hidden bg-[#101010] py-24 md:py-32">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <div className="mb-16 flex items-center gap-3">
          <span className="h-px w-10 bg-teal" />
          <span className="font-condensed text-base tracking-[0.35em] text-teal">CUSTOMER REVIEWS</span>
        </div>

        <h2 className="mb-14 font-display text-5xl leading-[0.9] uppercase sm:text-6xl md:text-7xl">
          <AnimatedWords text="Loved by the" />{" "}
          <span className="accent-teal">
            <AnimatedWords text="community" delay={0.15} />
          </span>
        </h2>

        <div className="relative min-h-[280px] md:min-h-[220px]">
          <Quote className="mb-6 text-teal" size={48} strokeWidth={1.5} />
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={index}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`review-${review.name.toLowerCase()}`}
            >
              <p className="font-condensed text-3xl leading-tight tracking-wide text-white sm:text-4xl md:text-5xl">
                "{review.quote}"
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-teal font-display text-xl text-black">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <p className="font-condensed text-xl tracking-widest">{review.name}</p>
                  <p className="text-sm text-zinc-500">{review.role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center gap-4">
          <button
            data-testid="review-prev"
            onClick={() => paginate(-1)}
            className="flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors duration-200 hover:border-teal hover:text-teal"
            aria-label="Previous review"
          >
            <ArrowLeft size={20} />
          </button>
          <button
            data-testid="review-next"
            onClick={() => paginate(1)}
            className="flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors duration-200 hover:border-teal hover:text-teal"
            aria-label="Next review"
          >
            <ArrowRight size={20} />
          </button>
          <div className="ml-4 flex gap-2">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDir(i > index ? 1 : -1);
                  setIndex(i);
                }}
                aria-label={`Go to review ${i + 1}`}
                className={`h-1.5 transition-all duration-300 ${
                  i === index ? "w-8 bg-teal" : "w-4 bg-white/20"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
